<template>
  <div
    v-if="questionsLength"
    data-test="multi-question-survey-wrapper"
    class="survey-wrapper bg-white"
    :class="[withBorder ? 'with-border p-3' : 'no-border pt-3', { 'survey-news-preview': isDisplayedInNews }]"
  >
    <div class="title-wrapper d-flex align-items-center mb-3">
      <sdui-icon class="sdui-icon icon-poll-24 text-primary primary-light-bg circle" />
      <div class="ml-2">
        <h3 class="h4 mb-0 gray-400 survey-preview-title">{{ $t('survey.voting.title') }}</h3>
        <p class="text-muted nano mb-0" data-test="survey-preview-title">{{ surveyPreviewTitle }}</p>
      </div>
      <div v-if="isNewsCreationPreview" class="text-right flex-grow-1">
        <SDropDown id="options-modal"
                   right
                   size=""
                   no-caret
                   class="add-dropdown"
                   variant="link"
                   data-test="survey-dropdown"
        >
          <template #button-content>
            <sdui-icon class="icon-more icon gray-300 survey-dropdown" variant="gray-300" />
            <span class="sr-only">{{ $t('survey.dropdown_hint') }}</span>
          </template>

          <b-dropdown-item-button data-test="dropdown-option-delete" @click="$emit('delete-survey-news-creation')">
            <sdui-icon class="icon-delete-o primary" variant="transparent" />
            <span class="ml-2 d-inline-block align-middle">{{ $t('global.delete') }}</span>
          </b-dropdown-item-button>
        </SDropDown>
      </div>
    </div>

    <sdui-survey-badges
      v-if="!isTranslationMode"
      :results-visibility="localSurvey.results_visibility"
      :is-anonymous="localSurvey.is_anonymous"
    />

    <div class="mt-3">
      <!-- MULTIPLE QUESTIONS & TRANSLATION MODAL IS OPEN -> show all questions -->
      <sdui-multi-question-survey-options-overview
        v-if="hasMultipleQuestions && isTranslationMode"
        :survey="localSurvey"
        :input-prefix="inputPrefix"
        :is-author="isSurveyAuthor"
        :is-preview-mode="!canVote || isSurveyPreviewMode"
        :show-results="showResults"
        @close-and-update="(data) => $emit('update', data)"
      />

      <!-- MULTIPLE QUESTIONS OR (SINGLE QUESTIONS IN NEWS-CREATION PREVIEW) -> display up to 4 questions-->
      <template v-else-if="hasMultipleQuestions || isNewsCreationPreview">
        <ol class="p-0 mb-0 text-muted nano">
          <li
            v-for="question in firstFourQuestions"
            :key="question.id"
            class="text-truncate survey-question-list-item"
            data-test="survey-multi-question-list-item"
          >
            {{ question.question }}
          </li>
        </ol>
        <!-- Display indicator if there are even more than 4 -->
        <span v-if="questionsLength > 4"
              class="text-muted nano"
              data-test="more-questions-indicator"
        >...</span>

        <b-button
          v-if="canVote"
          variant="primary"
          data-test="open-multiple-question-survey-answer-modal"
          data-cy="open-multiple-question-survey-answer-modal"
          class="mt-3"
          block
          @click="showSurveyModal(false)"
        >
          {{ this.$t('survey.start_voting') }}
        </b-button>
      </template>

      <!-- ONLY ONE QUESTION -> directly offer UI to vote on question -->
      <template v-else>
        <fieldset class="mb-0">
          <legend
            class="h3 black question"
            :class="isFirstQuestionTypeFreetext && canVote ? 'mb-0' : 'mb-3'"
            data-test="single-question-title"
          >
            {{ firstQuestion.question }}
          </legend>
          <div class="d-block">
            <sdui-survey-free-text-voting
              v-if="isFirstQuestionTypeFreetext && canVote"
              :survey-visibility="localSurvey.results_visibility"
              :user-has-voted="localSurvey.is_user_voted"
              :question="firstQuestion"
              data-test="survey-free-text-voting"
              @update="handleNewFreeTextInput"
            />

            <!-- EMPTY STATE (freetext question)-->
            <div
              v-else-if="!firstQuestion.options.length && !canVote"
              class="text-center text-muted small py-4"
              data-test="survey-option-empty-state"
            >
              {{ $t('survey.no_answers_yet') }}
            </div>

            <template v-else>
              <sdui-survey-option
                v-for="item in answerableSingleQuestionOptions"
                :key="item.uuid"
                :name="firstQuestion.id"
                :radio-btn-group-prefix="firstQuestion.id"
                :disabled="!canVote"
                :option="item"
                :show-results="showResults"
                :is-survey-public-release="localSurvey.results_visibility !== resultsVisibilityOptions.NONE"
                :is-question-type-free-text="isFirstQuestionTypeFreetext"
                :model-value="getSingleQuestionSurveyOptionValue(firstQuestion.uuid)"
                :is-multiple-answerable="isMultiAnswerableQuestion(firstQuestion)"
                :input-prefix="inputPrefix"
                :data-cy="item.has_user_option ? 'sdui-survey-single-question-user-option' : 'sdui-survey-single-question-option'"
                @change="singleQuestionOptionChanged"
                @validate-option="validateFreetextOption"
                @cancel-option="cancelUserOption"
              />
              <p
                v-if="isFirstQuestionTypeFreetext && firstQuestionAnswerLimitExceeded"
                class="text-muted"
                data-test="freetext-answer-limit-exceeded-indicator"
              >
                ...
              </p>
            </template>
          </div>
        </fieldset>

        <b-button
          v-if="canVote"
          v-btn-focus-restore-after-disable
          class="mt-3"
          block
          :class="$wait.waiting(`voting-${localSurvey.id}`) ? 'spinner-btn' : 'no-spinner'"
          :variant="isSubmitSingleQuestionBtnEnabled ? 'primary' : 'secondary'"
          :disabled="!isSubmitSingleQuestionBtnEnabled || $wait.waiting(`voting-${localSurvey.id}`)"
          data-cy="sdui-survey-submit"
          @click="submitVotes(singleQuestionSelectedValue, freeTextOptionUuids)"
        >
          {{ $t('survey.voting.vote_btn') }}
        </b-button>
      </template>

      <b-button
        v-if="showEvaluationSingleQuestionBtn || showEvaluationMultiQuestionBtn"
        :id="`preview-modal-btn-${survey.id}`"
        variant="link"
        data-test="open-multiple-question-survey-preview-modal"
        data-cy="open-multiple-question-survey-preview-modal"
        class="sdui-shadow mt-3 mb-2 border"
        block
        @click="showSurveyModal(true)"
      >
        <span v-if="isSurveyAuthor">{{ this.$t('survey.preview.view_evaluation') }}</span>
        <span v-else>{{ this.$t('survey.preview.view_answers') }}</span>
      </b-button>

      <div v-if="alreadyVoted && !isSurveyFinished" class="d-flex align-items-center justify-content-end pt-1">
        <div class="success-dark d-flex align-items-center justify-content-center nano">
          <sdui-icon class="icon-check-o nano success-dark" variant="" />
          <span :id="`survey-paricipated-${survey.id}`"
                tabindex="0"
                class="ml-1"
                data-cy="submit-survey-option-success"
          >{{
            $t('survey.voting.has_already_voted')
          }}</span>
        </div>
      </div>

      <div v-if="isSurveyFinished" class="text-center pt-1">
        <p class="black p-2 mb-0" data-test="survey-ended">{{ $t('survey.voting.survey_ended') }}</p>
      </div>
    </div>

    <!-- Modal to answer multiple questions -->
    <sdui-multi-question-survey-options-modal
      v-model="showMultiQuestionSurveyOptionsModal"
      :survey="localSurvey"
      :is-preview-mode="!canVote || isSurveyPreviewMode"
      :show-results="showResults"
      :input-prefix="inputPrefix"
      :is-author="isSurveyAuthor"
      :is-displayed-in-news="isDisplayedInNews"
      @hide="clearRouterQuery"
      @hidden="clearRouterQuery"
      @close="clearRouterQuery"
      @close-and-update="
        (data) => {
          clearRouterQuery;
          $emit('update', data);
        }
      "
    />
  </div>
</template>

<script>
import SduiSurveyOption from 'src/components/miscellaneous/ui/surveys/SduiSurveyOption.vue'
import constants from 'src/config/constants.ts'
import timerMixin from 'src/mixins/miscellaneous/timerMixin.js'
import SduiSurveyBadges from 'src/components/miscellaneous/ui/surveys/SduiSurveyBadges.vue'
import SduiMultiQuestionSurveyOptionsModal from 'src/components/miscellaneous/ui/surveys/SduiMultiQuestionSurveyOptionsModal.vue'
import SurveyMixin from 'src/mixins/channels/survey/surveyMixin.js'
import SduiMultiQuestionSurveyOptionsOverview from 'src/components/miscellaneous/ui/surveys/SduiMultiQuestionSurveyOptionsOverview.vue'
import SduiSurveyFreeTextVoting from 'src/components/miscellaneous/ui/surveys/SduiSurveyFreeTextVoting.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'SduiMultiQuestionSurvey',
  components: {
    SduiSurveyFreeTextVoting,
    SduiMultiQuestionSurveyOptionsOverview,
    SduiSurveyOption,
    SduiSurveyBadges,
    SduiMultiQuestionSurveyOptionsModal,
  },

  mixins: [timerMixin, SurveyMixin],
  props: {
    survey: {
      type: Object,
      required: true,
    },

    withBorder: {
      type: Boolean,
      default: false,
    },

    inputPrefix: {
      type: String,
      default: 'option',
    },

    isNewsCreationPreview: {
      type: Boolean,
      default: false,
    },

    isTranslationMode: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    localSurvey: {
      questions: [],
    },

    isSurveyPreviewMode: false,
    questionTypeOptions: [],
    singleQuestionSelectedValue: {},
    showMultiQuestionSurveyOptionsModal: false,
    staticIsSurveyFinished: false,
    resultsVisibilityOptions: constants.survey.resultsVisibility,
  }),

  computed: {
    isFreetextAnswerValid() {
      if (!this.isFirstQuestionTypeFreetext) return true
      const firstQuestion = this.singleQuestionSelectedValue[this.firstQuestion.uuid]
      return firstQuestion.new_option && firstQuestion.new_option.length <= constants.survey.maxCharacters
    },

    questionsLength() {
      return this.localSurvey?.questions?.length
    },

    hasMultipleQuestions() {
      return this.questionsLength > 1
    },

    firstQuestion() {
      return this.localSurvey.questions[0]
    },

    isFirstQuestionTypeFreetext() {
      return this.firstQuestion.type === constants.survey.question_type.FREE_TEXT
    },

    firstFourQuestions() {
      return this.localSurvey.questions.slice(0, 4)
    },

    surveyPreviewTitle() {
      // for surveys with one question, the title is the "question type" (+ expiration date if given)
      if (!this.hasMultipleQuestions) {
        const questionTypeLabel = this.questionTypeOptions.find((typeOption) => typeOption.key === this.firstQuestion?.type)?.label
        if (this.localSurvey.expires_at)
          return `${questionTypeLabel} • ${this.$t('survey.expires_at_survey_subtitle', { expirationDate: this.$dateFormat.date_time(this.localSurvey.expires_at) })}`
        return questionTypeLabel
      }

      // if there are multiple questions, our title is the amount of questions (+ expiration date if given)
      if (this.localSurvey.expires_at) {
        return `${this.$t('survey.number_of_questions_survey_subtitle', { numOfQuestions: this.localSurvey.questions?.length })} • ${this.$t('survey.expires_at_survey_subtitle', { expirationDate: this.$dateFormat.date_time(this.localSurvey.expires_at) })}`
      }

      return this.$t('survey.number_of_questions_survey_subtitle', { numOfQuestions: this.localSurvey.questions?.length })
    },

    singleQuestionOptions() {
      if (this.hasMultipleQuestions) return []

      if (this.isFirstQuestionTypeFreetext && !this.alreadyVoted) {
        return [...this.firstQuestion.options, this.createNewFreetextOption()]
      }

      if (this.firstQuestion.has_user_option && !this.alreadyVoted && !this.staticIsSurveyFinished) {
        return [...this.firstQuestion.options, this.createNewUserOption()]
      }

      return this.firstQuestion.options
    },

    answerableSingleQuestionOptions() {
      return this.singleQuestionOptions.filter((opt, i) => opt.name && this.areAnswerOptionsBelowLimit(i))
    },

    canVote() {
      return !this.isNewsCreationPreview && !this.isSurveyFinished && !this.alreadyVoted && this.$can('vote', this.localSurvey)
    },

    isSurveyFinished() {
      return this.localSurvey.is_over || this.$moment(this.currentDateTime).isAfter(this.$moment(this.localSurvey.expires_at))
    },

    alreadyVoted() {
      return this.localSurvey.is_user_voted
    },

    isSurveyAuthor() {
      return this.localSurvey.user_id === this.$me?.id
    },

    showResults() {
      const visibility = this.localSurvey.results_visibility
      return (
        this.isSurveyAuthor ||
        visibility === this.resultsVisibilityOptions.BEFORE ||
        (visibility === this.resultsVisibilityOptions.AFTER && this.alreadyVoted)
      )
    },

    isDisplayedInNews() {
      return ['feed', 'single-news'].includes(this.$route.name)
    },

    isSubmitSingleQuestionBtnEnabled() {
      return Object.keys(this.singleQuestionSelectedValue).length === this.localSurvey.questions?.length && this.isFreetextAnswerValid
    },

    freeTextOptionUuids() {
      return this.isFirstQuestionTypeFreetext
        ? this.singleQuestionSelectedValue[this.firstQuestion.uuid]?.options
        : [this.singleQuestionOptions.find((option) => option.has_user_option)?.uuid]
    },

    firstQuestionAnswerLimitExceeded() {
      return this.firstQuestion.options.length > constants.survey.maxVisibleFreetextAnswers
    },

    isSurveyDone() {
      return this.alreadyVoted || this.isSurveyFinished
    },

    showEvaluationMultiQuestionBtn() {
      return (this.isSurveyAuthor || this.isSurveyDone) && !this.isNewsCreationPreview && this.hasMultipleQuestions
    },

    shouldReceiverSeeFreetextResults() {
      return this.isSurveyDone && this.showResults && this.firstQuestionAnswerLimitExceeded
    },

    showEvaluationSingleQuestionBtn() {
      return (
        this.isFirstQuestionTypeFreetext && (this.isSurveyAuthor || this.shouldReceiverSeeFreetextResults) && !this.isNewsCreationPreview
      )
    },

    showVoters() {
      return String(this.$route.query.voters_list) === String(this.survey.id)
    },
  },

  emits: ['update', 'delete-survey-news-creation'],

  methods: {
    getSingleQuestionSurveyOptionValue(questionUuid) {
      // If the survey has already been voted on, the value is determined by option.is_chosen.
      if (!this.canVote) return
      return this.singleQuestionSelectedValue[questionUuid]?.options
    },

    handleNewFreeTextInput(value) {
      this.singleQuestionOptionChanged(this.singleQuestionOptions[0].uuid)
      this.validateFreetextOption({ answer: value }, this.singleQuestionOptions[0])
    },

    validateFreetextOption({ answer }, { uuid: freetextOptionUuid }) {
      if (!answer) return
      for (const questionObject of Object.values(this.singleQuestionSelectedValue)) {
        if (questionObject.options.includes(freetextOptionUuid)) {
          questionObject.new_option = answer
          return
        }
      }
    },

    singleQuestionOptionChanged(optionUuid, preventUserOptionDeselection = false) {
      this.singleQuestionSelectedValue = this.questionOptionChanged(
        this.singleQuestionSelectedValue,
        this.firstQuestion,
        optionUuid,
        preventUserOptionDeselection,
        this.isFirstQuestionTypeFreetext,
      )
    },

    async submitVotes(selectedOptions, freetextOptionUuidList = []) {
      const data = await this.submitAnswer(this.localSurvey.id, selectedOptions, freetextOptionUuidList, this.localSurvey.questions)
      clyTracking.trackSurvey(this.isDisplayedInNews ? 'news' : 'chatMessage').action.submit({ questionCount: this.localSurvey.questions.length })
      if (!data) return
      this.$emit('update', data)

      await this.$nextTick()
      const surveyParticipatedEl = document.querySelector(`#survey-paricipated-${this.survey.id}`)
      if (surveyParticipatedEl) surveyParticipatedEl.focus()
    },

    showSurveyModal(isPreview) {
      this.showMultiQuestionSurveyOptionsModal = true
      this.isSurveyPreviewMode = isPreview
    },

    clearRouterQuery() {
      this.showMultiQuestionSurveyOptionsModal = false
      this.$router.replace({ hash: this.$route.hash, query: {} })
    },

    areAnswerOptionsBelowLimit(index) {
      if (!this.isFirstQuestionTypeFreetext) return true
      return index < constants.survey.maxVisibleFreetextAnswers
    },
  },

  watch: {
    survey: {
      handler(newVal, oldVal) {
        if (this.$compare(oldVal, newVal)) return
        this.localSurvey = this.$clone(newVal)
      },

      immediate: true,
    },

    /* This watcher is needed because the isSurveyFinished computed is based on the currentDateTime and is therefore
     * updated every second. Even if the value of isSurveyFinished does not change, the singleQuestionOptions will be
     * recalculated every second. This causes the entire v-for loop and also the SduiSurveyOption component to be
     * re-rendered every second and the userOption modal to be closed every second. */
    isSurveyFinished: {
      handler(newVal, oldVal) {
        if (newVal === oldVal) return
        this.staticIsSurveyFinished = newVal
      },

      immediate: true,
    },

    showVoters(val) {
      this.showMultiQuestionSurveyOptionsModal = !!val
      this.isSurveyPreviewMode = !!val
    },
  },

  created() {
    this.questionTypeOptions = Object.keys(constants.survey.question_type).map((key) => ({
      key: constants.survey.question_type[key],
      label: this.$t(`survey.create.start.question_type.${key.toLowerCase()}`),
    }))
  },
}
</script>

<style scoped lang="scss">
.with-border {
  border: 1px solid $color-gray-100;
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba($color-black-rgb, 0.08);
}

.survey-wrapper.survey-news-preview {
  border-top: 1px solid $color-gray-100;
}

.title-wrapper {
  .sdui-icon {
    font-size: pxtorem(24);
    height: 36px;
    width: 36px;
  }
}

.survey-preview-title {
  line-height: pxtorem(20) !important;
}

.survey-question-list-item {
  list-style-position: inside;
}

.survey-dropdown {
  height: 28px !important;
  width: 28px !important;
  font-size: pxtorem(16) !important;
}
</style>
